(() => {
  if (customElements.get('mmc-price-comparison')) return;
  const sources = {
    b:{name:'Boulanger',hosts:['www.boulanger.com','boulanger.com']},
    d:{name:'Darty',hosts:['www.darty.com','darty.com']},
    cec:{name:'C&C',hosts:['shop.cec.fr']},
    am:{name:'Amazon.fr',hosts:['www.amazon.fr']},
    apple:{name:'Apple',hosts:['www.apple.com']},
    samsung:{name:'Samsung',hosts:['www.samsung.com']},
    google:{name:'Google Store',hosts:['store.google.com']},
    xiaomi:{name:'Xiaomi',hosts:['www.mi.com']},
    honor:{name:'Honor',hosts:['www.honor.com']},
    oneplus:{name:'OnePlus',hosts:['www.oneplus.com']}
  };
  const keys = Object.keys(sources);
  const locale = () => document.documentElement.lang.startsWith('en') ? 'en-GB' : 'fr-FR';
  const money = cents => new Intl.NumberFormat(locale(), {style:'currency', currency:'EUR'}).format(cents / 100);
  const verified = (read, key, now = Date.now()) => {
    try {
      const url = new URL(read.url);
      const source = sources[key];
      const age = now - Date.parse(read.checked_at);
      return read.status === 'verified' && read.condition === 'NEW' && ['GTIN_EXACT','MODEL_CAPACITY_COLOR'].includes(read.identity) && read.currency === 'EUR' && typeof read.price === 'number' &&
        Number.isFinite(read.price) && read.price > 0 && url.protocol === 'https:' && source?.hosts.includes(url.hostname) &&
        !url.username && !url.password && !url.port && age >= 0 && age < 15 * 60e3 && Date.parse(read.expires_at) > now;
    } catch { return false; }
  };
  class Comparison extends HTMLElement {
    connectedCallback() {
      this.reads = {};
      try { this.i18n = JSON.parse(this.querySelector('[data-comparison-i18n]')?.textContent || '{}'); } catch { this.i18n = {}; }
      this.text = (key, vars = {}) => Object.entries(vars).reduce((text, [name, value]) => text.split('__' + name.toUpperCase() + '__').join(value), this.i18n[key] || '');
      this.querySelector('.mmc-comparison__price').textContent = money(Number(this.dataset.price));
      this.refresh();
      this.timer = setInterval(() => {
        this.render();
        if (document.visibilityState === 'visible') this.refresh();
      }, 60000);
      this.onVisibility = () => { if (document.visibilityState === 'visible') { this.render(); this.refresh(); } };
      document.addEventListener('visibilitychange', this.onVisibility);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      clearTimeout(this.expiryTimer);
      this.request?.abort();
      document.removeEventListener('visibilitychange', this.onVisibility);
    }
    async refresh() {
      if (this.loading || !/^\d{12,14}$/.test(this.dataset.ean)) return;
      this.loading = true;
      const controller = this.request = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const url = new URL(this.dataset.endpoint);
        url.searchParams.set('ean', this.dataset.ean);
        const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
        const [response, productResponse] = await Promise.all([
          fetch(url, {signal:controller.signal, cache:'no-store', credentials:'omit'}),
          fetch(root + 'products/' + encodeURIComponent(this.dataset.handle) + '.js', {signal:controller.signal, cache:'no-store'})
        ]);
        if (!response.ok || !productResponse.ok) throw new Error('unavailable');
        const [data, product] = await Promise.all([response.json(), productResponse.json()]);
        const variant = product.variants?.find(value => String(value.id) === this.dataset.variant);
        if (!variant || String(variant.barcode) !== this.dataset.ean || !Number.isInteger(variant.price) || variant.price < 0) throw new Error('variant');
        if (!data.success || data.ean !== this.dataset.ean) throw new Error('reference');
        if (!this.isConnected) return;
        this.dataset.price = String(variant.price);
        this.querySelector('.mmc-comparison__price').textContent = money(variant.price);
        this.reads = variant.available === false ? {} : (data.retailers || {});
      } catch { this.reads = {}; }
      finally { clearTimeout(timeout); this.loading = false; if (this.isConnected) this.render(); }
    }
    render() {
      clearTimeout(this.expiryTimer);
      let expires = Infinity;
      const offers = keys.filter(key => verified(this.reads[key] || {}, key)).sort((a,b) => this.reads[a].price - this.reads[b].price || a.localeCompare(b));
      const hasComparison = offers.length > 0;
      const headline = this.querySelector('.mmc-comparison__headline');
      const badge = this.querySelector('.mmc-comparison__badge');
      headline.textContent = this.text('comparison_price');
      badge.hidden = true;
      badge.textContent = '';
      if (hasComparison) {
        const best = Math.round(this.reads[offers[0]].price * 100);
        const saving = best - Number(this.dataset.price);
        if (saving > 0) {
          headline.textContent = this.text('save', {amount:money(saving)});
          badge.textContent = '−' + (Math.floor(saving / best * 1000) / 10).toLocaleString(locale()) + ' %';
          badge.hidden = false;
        }
        for (const key of offers) {
          const card = this.querySelector(`[data-retailer="${key}"]`);
          card.parentElement.append(card);
        }
      }
      this.hidden = !hasComparison;
      this.dataset.hasComparison = String(hasComparison);
      for (const key of keys) {
        const card = this.querySelector(`[data-retailer="${key}"]`);
        const read = this.reads[key] || {};
        const offer = card.querySelector('.mmc-comparison__offer');
        card.querySelector('.mmc-comparison__seller').textContent = sources[key].name;
        const difference = card.querySelector('.mmc-comparison__difference');
        const checked = card.querySelector('time');
        const valid = verified(read, key);
        card.hidden = !valid;
        offer.hidden = !valid;
        offer.removeAttribute('href');
        offer.textContent = '';
        difference.textContent = '';
        delete difference.dataset.saving;
        checked.textContent = '';
        checked.removeAttribute('datetime');
        checked.removeAttribute('title');
        if (!valid) continue;
        const price = Math.round(read.price * 100);
        const saving = price - Number(this.dataset.price);
        offer.href = read.url;
        offer.textContent = money(price);
        offer.setAttribute('aria-label', this.text('offer_link', {retailer:sources[key].name,amount:money(price)}));
        difference.textContent = saving > 0 ? this.text('saving', {amount:money(saving)}) :
          saving === 0 ? this.text('same_price') : this.text('more', {amount:money(-saving)});
        difference.dataset.saving = String(saving > 0);
        const date = new Date(read.checked_at);
        checked.dateTime = date.toISOString();
        checked.textContent = this.text('checked', {date:date.toLocaleDateString(locale(), {timeZone:'Europe/Paris', day:'2-digit', month:'2-digit'}),time:date.toLocaleTimeString(locale(), {timeZone:'Europe/Paris', hour:'2-digit', minute:'2-digit'})});
        checked.title = date.toLocaleString(locale(), {timeZone:'Europe/Paris'});
        expires = Math.min(expires, Date.parse(read.expires_at));
      }
      if (Number.isFinite(expires)) this.expiryTimer = setTimeout(() => this.render(), Math.max(0, expires - Date.now()) + 20);
    }
  }
  customElements.define('mmc-price-comparison', Comparison);
})();

